import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Container, Spinner, Alert } from "react-bootstrap";
import { FaCheckCircle, FaExclamationTriangle, FaSync } from "react-icons/fa";
import { lightspeedService } from "../../../services/lightspeedService";
import {
  handleLightspeedError as convertUnknownErrorToLightspeedError,
  LightspeedError,
  LightspeedErrorHandler,
  LightspeedErrorCodes,
  getLightspeedErrorMessage,
} from "../../utils/lightspeedErrorHandler";
import { LightspeedOAuthCallback } from "../../types/lightspeed.types";
import "./lightspeed-components.scss";

interface OAuthCallbackHandlerProps {
  onComplete?: (success: boolean, message?: string) => void;
  redirectPath?: string;
}

export const OAuthCallbackHandler: React.FC<OAuthCallbackHandlerProps> = ({
  onComplete,
  redirectPath = "/dashboard/integrations/lightspeed",
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing"
  );
  const [message, setMessage] = useState<string>("Connecting to Lightspeed...");
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [lightspeedError, setLightspeedError] =
    useState<LightspeedError | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const handleLightspeedError = useCallback(
    (error: LightspeedError) => {
      setStatus("error");
      setLightspeedError(error);
      setMessage(error.userMessage);
      onComplete?.(false, error.userMessage);
      try {
        sessionStorage.removeItem("lightspeed_oauth_session_id");
      } catch {}

      if (!error.retryable) {
        setTimeout(() => {
          navigate(redirectPath, {
            state: { error: true, message: error.userMessage },
          });
        }, 5000);
      }
    },
    [navigate, onComplete, redirectPath]
  );

  useEffect(() => {
    const processCallback = async () => {
      try {
        setStatus("processing");
        setMessage("Processing your Lightspeed connection...");
        setCurrentStep(0);
  
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
  
        const storedSessionId = sessionStorage.getItem('lightspeed_oauth_session_id') || undefined;
        const callbackData: LightspeedOAuthCallback = {
          code: code || "",
          state: state || "",
          sessionId: storedSessionId,
          error: error || undefined,
          errorDescription: errorDescription || undefined
        };

        if (callbackData.error) {
          const lightspeedErr =
            LightspeedErrorHandler.mapOAuthErrorToLightspeedError(
              callbackData.error,
              callbackData.errorDescription
            );
          throw lightspeedErr;
        }

        if (!callbackData.code) {
          throw LightspeedErrorHandler.createError(
            LightspeedErrorCodes.OAUTH_INVALID_CODE
          );
        }

        if (!callbackData.state) {
          throw LightspeedErrorHandler.createError(
            LightspeedErrorCodes.OAUTH_INVALID_STATE
          );
        }

        setMessage("Exchanging authorization code…");
        setCurrentStep(1);
        const response = await lightspeedService.handleCallback(callbackData);
        

        if (response.success) {
          setMessage("Finalizing store connection…");
          setCurrentStep(2);
          setStatus("success");
          setMessage("Successfully connected to Lightspeed!");
          setCurrentStep(3);
          setRetryCount(0);
          onComplete?.(true, "Connection successful");
          try {
            sessionStorage.removeItem("lightspeed_oauth_session_id");
          } catch {}

          setTimeout(() => {
            try {
              if (window.opener && window.name === "lightspeed_oauth_tab") {
                window.close();
              }
            } catch {}
          }, 2000);
        } else {
          throw new Error(response.error || "Failed to complete connection");
        }
      } catch (error) {
        if (error instanceof Error && 'code' in error) {
          handleLightspeedError(error as unknown as LightspeedError);
        } else {
          const lightspeedErr = convertUnknownErrorToLightspeedError(error);
          handleLightspeedError(lightspeedErr);
        }
      }
    };

    processCallback();
  }, [
    searchParams,
    navigate,
    onComplete,
    redirectPath,
    retryCount,
    handleLightspeedError,
  ]);

  const handleRetry = () => {
    if (retryCount < maxRetries) {
      setRetryCount((prev) => prev + 1);
      setStatus("processing");
      setMessage("Retrying connection...");
      setLightspeedError(null);
    }
  };

  const handleDismissError = () => {
    setLightspeedError(null);
  };

  return (
    <Container className="oauth-callback-container">
      {status === "processing" && (
        <div className="oauth-processing" aria-live="polite">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="message-row d-flex align-items-center gap-2">
            <p className="processing-message mb-0">{message}</p>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => window.location.reload()}
              aria-label="Refresh to check connection status"
            >
              Refresh
            </button>
          </div>
          <div className="processing-steps">
            <ul className="list-unstyled mb-0">
              <li>{currentStep >= 0 ? "✓" : "•"} Authorized by Lightspeed</li>
              <li>
                {currentStep >= 1 ? "✓" : "•"} Exchanging authorization code
              </li>
              <li>
                {currentStep >= 2 ? "✓" : "•"} Finalizing store connection
              </li>
              <li>{currentStep >= 3 ? "✓" : "•"} Connected</li>
            </ul>
          </div>
          {retryCount > 0 && (
            <p className="retry-info">
              Attempt {retryCount} of {maxRetries}
            </p>
          )}
        </div>
      )}

      {status === "success" && (
        <div className="oauth-result-view success" aria-live="polite">
          <div className="icon-container">
            <FaCheckCircle className="result-icon success-icon" />
          </div>
          <h4 className="result-message">{message}</h4>
          <p className="result-subtitle">You will be redirected shortly.</p>
        </div>
      )}

      {status === "error" && lightspeedError && (
        <div className="oauth-result-view error">
          <div className="icon-container">
            <FaExclamationTriangle className="result-icon error-icon" />
          </div>
          <h4 className="result-message">
            {getLightspeedErrorMessage(lightspeedError.code)}
          </h4>
          <p className="result-subtitle">{lightspeedError.userMessage}</p>

          <div className="oauth-actions">
            {lightspeedError.retryable && retryCount < maxRetries && (
              <Button variant="primary" onClick={handleRetry}>
                <FaSync className="me-1" /> Try Again
              </Button>
            )}
            <Button variant="outline-secondary" onClick={handleDismissError}>
              Close
            </Button>
          </div>
        </div>
      )}

      {status === "error" && !lightspeedError && (
        <Alert variant="danger" className="oauth-fallback-error">
          <i className="bi bi-exclamation-triangle-fill"></i>
          {message}
          <div className="oauth-actions">
            <button
              className="btn btn-outline-primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                navigate(redirectPath);
              }}
            >
              Go Back
            </button>
          </div>
        </Alert>
      )}
    </Container>
  );
};
